"use client";
import {
  AspectRatio,
  Box,
  colors,
  Flex,
  Grid,
  Heading,
  HeadingProps,
  IconButtonBS,
  IconButtonProps,
  Skeleton,
  Stack,
  StackProps,
  useBreakpointValue
} from "@biblioteksentralen/react";
import { LCWork } from "@libry-content/integrations";
import { KulturfondVideo } from "@libry-content/types";
import { motion } from "framer-motion";
import { sift } from "radash";
import { useId, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";
import { useMount } from "react-use";
import useSWR from "swr";
import { TypeTag } from "../../searchAndCollection/components/DocumentTypes";
import ItemPreview from "../../searchAndCollection/components/works/ItemPreview";
import { getSearchConfig } from "../../searchAndCollection/searchApi/searchConfig";
import { getWorks } from "../../searchAndCollection/searchApi/searchWorks";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { useCommonData } from "../layout/CommonDataProvider";

export type KulturfondBrowserProps = {
  kulturfondVideoer: KulturfondVideo[];
  headingProps?: HeadingProps;
} & StackProps;

export const KulturfondBrowserCSR = (props: KulturfondBrowserProps) => {
  // siden fetching av verk gjentas ved endring av pageSize, fallbacker vi til undefined for å unngå unødvendig fetching før breakpoint er bestemt
  const pageSize = useBreakpointValue({ base: 1, md: 2, lg: 3 }, { fallback: undefined });

  if (!pageSize) return null;
  return <Content {...props} pageSize={pageSize} />;
};

const Content = ({
  kulturfondVideoer,
  pageSize,
  headingProps,
  ...stackProps
}: KulturfondBrowserProps & { pageSize: number }) => {
  const { t } = useTranslation();
  const headingId = useId();
  const { site } = useCommonData();
  const searchConfig = getSearchConfig(site);

  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const lastPage = Math.floor((kulturfondVideoer.length - 1) / pageSize);
  const isFirstPage = currentPageNumber === 0;
  const isLastPage = currentPageNumber === lastPage;

  // Used to set bring focus to new video when paginating
  const [hasPaginated, setHasPaginated] = useState(false);

  const currentVideos = kulturfondVideoer?.slice(currentPageNumber * pageSize, (currentPageNumber + 1) * pageSize);
  const currentWorkIds = sift(currentVideos.map((video) => video.workId));

  const paginate = (newPage: number) => {
    setCurrentPageNumber(newPage);
    setHasPaginated(true);
  };

  const key = { page: currentPageNumber, pageSize };

  const { data: currentWorks } = useSWR(
    key,
    async (key) => {
      return await getWorks(searchConfig, currentWorkIds);
    },
    { revalidateIfStale: false, revalidateOnFocus: false }
  );

  return (
    <Stack as="section" aria-labelledby={headingId} gap="1rem" {...stackProps}>
      <Heading as="h2" fontSize="4xl" id={headingId} {...headingProps}>
        {t("Se en bokanbefaling")}
      </Heading>
      <Grid
        gap=".5rem"
        alignItems="center"
        gridTemplateAreas={{
          base: `
            "videos videos" 
            "button-previous button-next"
          `,
          sm: `"button-previous videos button-next"`,
        }}
        gridTemplateColumns={{ base: "1fr 1fr", sm: "auto 1fr auto" }}
      >
        {!isFirstPage && (
          <ChevronButton gridArea="button-previous" direction="left" onClick={() => paginate(currentPageNumber - 1)} />
        )}
        <Grid gridArea="videos" minH="24rem" flex="1" gridTemplateColumns={`repeat(${pageSize}, 1fr)`} gap=".5rem">
          {currentVideos.length
            ? currentVideos.map((video, index) => {
                const work = currentWorks?.find((work) => work.id === video.workId);
                if (!work)
                  return (
                    <Skeleton
                      key={index}
                      borderRadius="lg"
                      variant="pulse"
                      css={{
                        "--start-color": colors.grey15,
                        "--end-color": colors.grey05,
                      }}
                    />
                  );

                return (
                  <Box key={video.workId} background="blackAlpha.50" borderRadius="lg">
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.075, type: "spring", stiffness: 300, damping: 15 }}
                      style={{ height: "100%" }}
                    >
                      <VideoAnbefaling autoFocus={index === 0 && hasPaginated} work={work} video={video} />
                    </motion.div>
                  </Box>
                );
              })
            : new Array(pageSize).fill(null).map((_, index) => (
                <Skeleton
                  key={index}
                  borderRadius="lg"
                  variant="pulse"
                  css={{
                    "--start-color": colors.grey15,
                    "--end-color": colors.grey05,
                  }}
                />
              ))}
        </Grid>
        {!isLastPage && (
          <ChevronButton gridArea="button-next" direction="right" onClick={() => paginate(currentPageNumber + 1)} />
        )}
      </Grid>
    </Stack>
  );
};

const ChevronButton = ({
  onClick,
  direction,
  ...iconButtonProps
}: { onClick: () => void; direction: "left" | "right" } & IconButtonProps) => {
  const { t } = useTranslation();

  return (
    <IconButtonBS
      size="lg"
      variant="secondary"
      onClick={onClick}
      aria-label={direction === "right" ? t("Se flere videoer") : t("Se forrige videoer")}
      {...iconButtonProps}
    >
      {direction === "right" ? <ChevronRight strokeWidth="2.4" /> : <ChevronLeft strokeWidth="2.4" />}
    </IconButtonBS>
  );
};

const VideoAnbefaling = (props: { video: KulturfondVideo; work: LCWork; autoFocus: boolean }) => {
  const { t, ts } = useTranslation();
  const [iframeIsLoading, setIframeLoading] = useState(true);
  const headingId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useMount(() => {
    props.autoFocus && ref.current?.focus();
  });

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
      background={colors.grey10}
      borderRadius="lg"
      overflow="hidden"
      aria-labelledby={headingId}
      as="article"
      ref={ref}
      tabIndex={-1}
    >
      <AspectRatio
        overflow="hidden" // to prevent overflow from blur-animation
        ratio={1.1}
        background={`linear-gradient(190deg, ${colors.grey30}, ${colors.grey45})`}
      >
        <Box
          asChild
          title={t("Videoavspiller")}
          opacity={iframeIsLoading ? 0 : 1}
          filter={`blur(${iframeIsLoading ? "1rem" : "0"})`}
          transition="opacity .3s, filter .6s"
        >
          <iframe
            src={props.video.videoUrl}
            frameBorder={0}
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            onLoad={() => setIframeLoading(false)}
          />
        </Box>
      </AspectRatio>
      {props.work ? (
        <ItemPreview
          id={headingId}
          compact
          padding=".75rem"
          as={Box}
          item={props.work}
          height="auto"
          minHeight={undefined}
          childrenTop={
            <Flex gap=".25rem" flexWrap="wrap">
              {props.work.ageGroups.map((ageGroup) => (
                <TypeTag key={ageGroup.id} backgroundColor={colors.grey80} color="white">
                  {ts(ageGroup.name)}
                </TypeTag>
              ))}
              {props.work.getTransformedGenres(ts).map((genre) => (
                <TypeTag key={genre} background="white" color={colors.grey90}>
                  {genre}
                </TypeTag>
              ))}
            </Flex>
          }
        />
      ) : (
        <Skeleton
          margin="1rem"
          borderRadius="lg"
          variant="pulse"
          css={{
            "--start-color": colors.grey30,
            "--end-color": colors.grey15,
          }}
          height="8rem"
        />
      )}
    </Flex>
  );
};
